import React, { useCallback, useEffect } from "react";
import JiraServiceDeskCounts from "components/jira-service-desk-counts/jira-service-desk-counts";
import SchemeCounts from "components/scheme-counts/scheme-counts";
import JiraSoftwareCounts from "components/jira-software-counts/jira-software-counts";
import { useDispatch, useSelector } from "react-redux";
import Axios from "axios";
import { toast } from "react-toastify";
import {
    updateJWT,
    updateSchemeData,
    updateServiceDeskData,
    updateSoftwareData,
    updateInActiveResourceData,
} from "store/cloud-counter/CloudCounterSlice";
import Exports from "components/exports/exports";
import InActiveResources from "components/inactive-resource";
import { cloudCounterObject } from "constant/cloud-counter";

const MainCloudCounter = ({ activeTab, setActiveTab }) => {
    const dispatch = useDispatch();
    const tokenVal = useSelector((state) => state.project.bodyToken);
    const {
        software_counts,
        service_desk_counts,
        scheme_counts,
        inactive_resources,
    } = useSelector((state) => state.cloudCounter);
    const [loading, setLoading] = React.useState(false); // Add loading state

    const requestResources = useCallback(
        async (token) => {
            setLoading(true); // Start loading
            const resources = [
                "scheme",
                "software",
                "servicedesk",
                "inactiveresources",
            ];

            try {
                const requests = resources.map(async (resource) => {
                    try {
                        const res = await Axios.post(`/jira/${resource}/`, {
                            token,
                        });
                        return {
                            response: res.data,
                            resource,
                        };
                    } catch (err) {
                        toast.error(
                            `Unable to get ${resource} data at the moment. Kindly try again by refreshing the page`,
                            { appearance: "error" }
                        );
                        return null; // Ensure failed requests don't break Promise.all
                    }
                });

                const results = await Promise.all(requests);

                results.forEach((result) => {
                    if (!result) return; // Skip failed requests

                    const { previous, ...data } = result.response;
                    let id = 0;

                    const payload = [
                        { data, date: new Date().toISOString(), id: id++ },
                        ...previous.map((data) => ({ ...data, id: id++ })),
                    ];

                    switch (result.resource) {
                        case "software":
                            dispatch(updateSoftwareData(payload));
                            break;
                        case "servicedesk":
                            dispatch(updateServiceDeskData(payload));
                            break;
                        case "scheme":
                            dispatch(updateSchemeData(payload));
                            break;
                        case "inactiveresources":
                            dispatch(updateInActiveResourceData(payload));
                            break;
                        default:
                            break;
                    }
                });
            } catch (error) {
                toast.error(
                    "An unexpected error occurred while fetching resources.",
                    { appearance: "error" }
                );
            } finally {
                setLoading(false); // Stop loading when all requests are done
            }
        },
        [dispatch]
    );

    useEffect(() => {
        const location = window.location;
        const searchObj = new URLSearchParams(location.search.substr(1));
        const jwt = tokenVal.token;
        const lic = searchObj.get("lic");
        console.log(`APP_MODE is ${process.env.MODE} `);
        if (
            process.env.REACT_APP_MODE === "development" ||
            process.env.REACT_APP_NODE_ENV === "dev"
        ) {
            dispatch(updateJWT(jwt));
            requestResources(jwt);
        } else {
            if (!lic || lic.toLowerCase() !== "active") {
                toast.error(
                    `Unable to verify your license. Kindly reach out to your site admin`,
                    { appearance: "error" }
                );
            } else {
                dispatch(updateJWT(jwt));
                requestResources(jwt);
            }
        }
    }, [requestResources, tokenVal.token, dispatch]);

    const getViewData = useCallback(() => {
        switch (activeTab) {
            case "software":
                return {
                    data: software_counts || [],
                    title: "Jira Software",
                };
            case "servicedesk":
                return {
                    data: service_desk_counts || [],
                    title: "Jira Service Management",
                };
            case "scheme":
                return { data: scheme_counts || [], title: "Scheme" };
            case "inactiveresources":
                return {
                    data: inactive_resources || [],
                    title: "Inactive Resources",
                };
            default:
                return { data: [], title: "" };
        }
    }, [
        activeTab,
        software_counts,
        service_desk_counts,
        scheme_counts,
        inactive_resources,
    ]);

    const componentMapper = () => {
        switch (activeTab) {
            case "scheme":
                return <SchemeCounts />;
            case "management":
                return <JiraServiceDeskCounts />;
            case "inactiveresources":
                return <InActiveResources />;
            default:
                return <JiraSoftwareCounts />;
        }
    };

    return (
        <>
            <div className="mt-10">
                <div className="flex justify-between w-full items-center pl-10">
                    <div>
                        <p className="font-medium text-[20px] leading-[30px] text-[#595959] dark:text-white">
                            {cloudCounterObject[activeTab]}
                        </p>
                        {activeTab === "inactiveresources" && (
                            <p className="text-base text-[#595959] mt-2 dark:text-white">
                                Overview of all resources that have been
                                inactive for over 90 days
                            </p>
                        )}
                    </div>
                    <Exports info={getViewData()} />
                </div>

                {loading ? (
                    <div
                        style={{ height: "calc(100vh - 200px)" }}
                        className="flex flex-col items-center justify-center "
                    >
                        <div className="w-16 h-16 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                        <p className="mt-4 text-lg text-white font-semibold">
                            Hang tight! We're gathering your resources.
                        </p>
                    </div>
                ) : (
                    <div className="pl-10 mt-8">
                        {activeTab === "software" ? (
                            <JiraSoftwareCounts />
                        ) : (
                            componentMapper()
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default MainCloudCounter;
